import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertCircle, Loader2 } from "lucide-react";
import { Link } from "@tanstack/react-router";

import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { portalFetch } from "@/lib/portalApi";

export type AuthorizedConnection = {
  connectorId: number;
  connectorName: string;
  properties: Record<string, string>;
};

type AuthorizedConnectionSelectorProps = {
  organisationId: string;
  connectorType: string; // 'Xero', 'Vend', etc.
  value: number | null;
  onChange: (connectorId: number, properties: Record<string, string>) => void;
  disabled?: boolean;
};

export function AuthorizedConnectionSelector({
  organisationId,
  connectorType,
  value,
  onChange,
  disabled,
}: AuthorizedConnectionSelectorProps) {
  const { token } = useAuth();
  const [connections, setConnections] = useState<AuthorizedConnection[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!organisationId || !connectorType || !token) return;

    const fetchConnections = async () => {
      try {
        setLoading(true);

        const data = await portalFetch<{ connections: AuthorizedConnection[] }>(
          `/api/v1/gateway/business2cloud/api/v1/connectors/authorized?organisationId=${organisationId}&type=${connectorType}`,
          { token },
        );
        setConnections(data.connections ?? []);

        // Auto-select if only one connection available
        if (!value && data.connections?.length === 1) {
          const conn = data.connections[0];
          onChange(conn.connectorId, conn.properties);
        }
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "An error occurred");
      } finally {
        setLoading(false);
      }
    };

    fetchConnections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisationId, connectorType, token]);

  const handleValueChange = (stringValue: string) => {
    const connectorId = parseInt(stringValue, 10);
    const connection = connections.find((c) => c.connectorId === connectorId);
    if (connection) {
      onChange(connectorId, connection.properties);
    }
  };

  if (loading) {
    return (
      <div className="space-y-2">
        <Label>{connectorType} Connection</Label>
        <div className="flex h-11 items-center gap-2 rounded-xl border border-slate-300 bg-slate-50 px-4 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading authorized connections...
        </div>
      </div>
    );
  }

  if (connections.length === 0) {
    return (
      <div className="space-y-3">
        <Label>{connectorType} Connection</Label>
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="mt-0.5 h-5 w-5 flex-none text-amber-600" />
            <div className="space-y-2">
              <p className="text-sm font-medium text-amber-800">
                No authorized {connectorType} connections found
              </p>
              <p className="text-xs text-amber-700">
                You need to authorize a {connectorType} connection before you
                can use it as a target.
              </p>
              <Link
                to="/portal/connectors/$typeSlug"
                params={{ typeSlug: connectorType.toLowerCase() }}
                className="inline-block text-sm font-semibold text-primary underline hover:text-primary"
              >
                Authorize {connectorType} connection
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Label htmlFor="authorized-connection">{connectorType} Connection</Label>
      <Select
        value={value?.toString() ?? ""}
        onValueChange={handleValueChange}
        disabled={disabled}
      >
        <SelectTrigger id="authorized-connection">
          <SelectValue placeholder={`Select ${connectorType} connection`} />
        </SelectTrigger>
        <SelectContent>
          {connections.map((connection) => (
            <SelectItem
              key={connection.connectorId}
              value={connection.connectorId.toString()}
            >
              <span className="font-medium">{connection.connectorName}</span>
              {connection.properties?.["xero.tenantName"] && (
                <span className="ml-2 text-xs text-slate-500">
                  ({connection.properties["xero.tenantName"]})
                </span>
              )}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-xs text-slate-500">
        Select an existing authorized {connectorType} connection to use as the
        target
      </p>
    </div>
  );
}
